import { Link } from "@tanstack/react-router";
import { usePageTitle } from "@/routes/__root";
import { BrainCircuit, PlayCircle, Handshake, BookOpen, ArrowRight, Check } from "lucide-react";
import heroImage from "@/assets/hero-townhouse.jpg";
import { Button } from "@/components/ui/button";
import { Section, SectionHeading } from "@/components/site/Section";
import { useQuery } from "@tanstack/react-query";
import { funnelSteps } from "@/lib/site-data";
import { api } from "@/lib/api";
import { money } from "@/lib/format";
import { useAuth } from "@/lib/hooks";
import { isMobile } from "@/lib/config";


const pillars = [
  {
    icon: BookOpen,
    title: "The Blueprint",
    body: "Our Tier by Tier property book — the foundations of a UK portfolio, free to download.",
    to: "/blueprint" as const,
  },
  {
    icon: PlayCircle,
    title: "Academy Portal",
    body: "£10/month for a video library segmented by investor level, plus the AI Deal Analyzer.",
    to: "/academy" as const,
  },
  {
    icon: BrainCircuit,
    title: "Mentorship Schemes",
    body: "Bronze to Platinum VIP — 1-on-1 hours, priority sourcing and JV structuring.",
    to: "/mentorship" as const,
  },
  {
    icon: Handshake,
    title: "Deal Sourcing",
    body: "A hands-off service that finds, negotiates and packages the deal for you.",
    to: "/deal-sourcing" as const,
  },
];

export function HomePage() {
  usePageTitle("");
  const { isAuthenticated } = useAuth();
  const { data: schemes = [] } = useQuery({ queryKey: ["mentorship", "packages"], queryFn: api.mentorship.packages });
  return (
    <>
      <section className="relative isolate overflow-hidden">
        <img
          src={heroImage}
          alt="Georgian London townhouse terrace lit at dusk"
          width={1920}
          height={1088}
          className="absolute inset-0 -z-10 size-full object-cover"
        />
        <div className="absolute inset-0 -z-10 bg-[linear-gradient(100deg,oklch(0.15_0.035_262/0.94),oklch(0.15_0.035_262/0.65))]" />
        <div className="mx-auto max-w-6xl px-5 py-28 md:py-40">
          <p className="eyebrow text-gold">Sir Levantine Limited</p>
          <h1 className="mt-5 max-w-3xl text-4xl leading-[1.05] text-ink-foreground md:text-7xl">
            Build a UK property portfolio with a <span className="text-gilded">system</span>, not a
            hunch.
          </h1>
          <p className="mt-7 max-w-xl text-lg text-ink-foreground/75">
            Education, AI-powered deal analysis and hands-on mentorship — from your first buy-to-let
            to a commercial portfolio.
          </p>
          <div className="mt-10 flex flex-wrap gap-3">
            <Button asChild variant="gold" size="xl">
              {isAuthenticated ? (
                <Link to="/dashboard">Go to your dashboard</Link>
              ) : (
                <Link to="/signup">{isMobile ? "Join the Academy" : "Join the Academy — £10/mo"}</Link>
              )}
            </Button>
            <Button asChild variant="outlineLight" size="xl">
              <Link to="/blueprint">Get the free Blueprint</Link>
            </Button>
          </div>
          <dl className="mt-16 grid max-w-2xl grid-cols-2 gap-8 sm:grid-cols-3">
            {[
              ["£10/mo", "Portal membership"],
              ["4 tiers", "Bronze to Platinum VIP"],
              ["3–6%", "Sourcing fee scale"],
            ].map(([value, label]) => (
              <div key={label}>
                <dt className="font-display text-3xl text-gold">{value}</dt>
                <dd className="mt-1 text-xs uppercase tracking-widest text-ink-foreground/60">
                  {label}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      <Section>
        <SectionHeading
          eyebrow="What we do"
          title="One academy, four ways to move forward"
          subtitle="Every route starts with the same foundation: understand the numbers before you commit capital."
        />
        <div className="mt-14 grid gap-6 md:grid-cols-2">
          {pillars.map((p) => (
            <Link
              key={p.title}
              to={p.to}
              className="group rounded-lg border border-border bg-card p-8 shadow-card transition-all hover:-translate-y-1 hover:border-gold/50"
            >
              <p.icon className="size-6 text-gold" />
              <h3 className="mt-5 text-2xl">{p.title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{p.body}</p>
              <span className="mt-6 inline-flex items-center gap-2 text-sm font-medium">
                Explore
                <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" />
              </span>
            </Link>
          ))}
        </div>
      </Section>

      <Section ink>
        <div className="grid gap-14 lg:grid-cols-2 lg:items-center">
          <div>
            <SectionHeading
              light
              eyebrow="The flagship tool"
              title="The AI Deal Analyzer"
              subtitle="Paste a Rightmove link. Within seconds you get a refurbishment estimate, projected rental yield, and a drafted offer letter you can send the same day."
            />
            <div className="mt-8 space-y-3">
              {[
                "Refurb cost estimates benchmarked by region",
                "Gross and net yield, plus stress-tested cashflow",
                "Comparable sold prices from Land Registry data",
                "Auto-drafted, negotiation-ready offer letters",
              ].map((f) => (
                <div key={f} className="flex items-start gap-3 text-sm text-ink-foreground/80">
                  <Check className="mt-0.5 size-4 shrink-0 text-gold" />
                  {f}
                </div>
              ))}
            </div>
            <Button asChild variant="gold" size="lg" className="mt-9">
              <Link to="/deal-analyzer">Try the Deal Analyzer</Link>
            </Button>
          </div>

          <div className="rounded-lg border border-ink-foreground/15 bg-ink-foreground/5 p-6 shadow-elevated">
            <p className="eyebrow text-gold">Sample analysis</p>
            <p className="mt-3 font-display text-2xl text-ink-foreground">
              3-bed terrace, Bolton BL3
            </p>
            <div className="mt-6 space-y-3 text-sm">
              {[
                ["Asking price", "£135,000"],
                ["Estimated refurb", "£18,400"],
                ["Post-works value", "£182,000"],
                ["Monthly rent (est.)", "£975"],
                ["Gross yield", "8.7%"],
                ["Suggested offer", "£121,500"],
              ].map(([k, v]) => (
                <div
                  key={k}
                  className="flex items-center justify-between border-b border-ink-foreground/10 pb-3 text-ink-foreground/70"
                >
                  <span>{k}</span>
                  <span className="font-medium text-ink-foreground">{v}</span>
                </div>
              ))}
            </div>
            <p className="mt-5 text-xs text-ink-foreground/50">
              Illustrative figures. Every analysis is an estimate, not financial advice.
            </p>
          </div>
        </div>
      </Section>

      <Section>
        <SectionHeading
          center
          eyebrow="The journey"
          title="From first download to full portfolio"
        />
        <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {funnelSteps.map((s) => (
            <div key={s.stage} className="rounded-lg border border-border bg-card p-7 shadow-card">
              <p className="eyebrow text-gold">{s.stage}</p>
              <h3 className="mt-4 text-xl">{s.title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{s.body}</p>
            </div>
          ))}
        </div>
      </Section>

      <Section className="pt-0">
        <SectionHeading
          eyebrow="Scheme packages"
          title="Upgrade when you are ready to move faster"
          subtitle="All schemes sit on top of the £10/mo Academy Portal membership."
        />
        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {schemes.map((s) => (
            <div
              key={s.id}
              className={
                s.isFeatured
                  ? "rounded-lg border border-gold bg-card p-7 shadow-elevated"
                  : "rounded-lg border border-border bg-card p-7 shadow-card"
              }
            >
              <p className="eyebrow text-gold">{s.name}</p>
              <p className="mt-3 font-display text-3xl">{money(s.price, s.currency)}</p>
              <p className="mt-2 text-xs text-muted-foreground">{s.subtitle}</p>
            </div>
          ))}
        </div>
        <Button asChild variant="ink" size="lg" className="mt-10">
          <Link to="/mentorship">Compare all schemes</Link>
        </Button>
      </Section>
    </>
  );
}
